import {
  Condition,
  ExplicitOperator,
  FieldMatchNode,
  MatchExpression,
  MatchExpressionMultiMatch,
  QueryParameters,
  QueryParameterReference,
  isFieldMatchNode
} from './types.js'

export interface GraphOptimizationOptions {
  dedupeOrBranches?: boolean
  mergeOrFieldParameters?: boolean
}

const DEFAULT_OPTIONS: Required<GraphOptimizationOptions> = {
  dedupeOrBranches: true,
  mergeOrFieldParameters: false
}

export function optimizeQueryGraph(
  graph: MatchExpression,
  parameters: QueryParameters,
  options: GraphOptimizationOptions = {}
): MatchExpression {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options }
  const optimized = optimizeCondition(graph, parameters, mergedOptions)
  return ensureMatchExpression(optimized ?? graph)
}

function optimizeCondition(
  condition: Condition,
  parameters: QueryParameters,
  options: Required<GraphOptimizationOptions>
): Condition | null {
  if (isFieldMatchNode(condition)) {
    return condition
  }

  const optimizedExpression = optimizeMatchExpression(condition, parameters, options)

  const operator = getOperator(optimizedExpression)
  const childConditions = optimizedExpression.conditions

  if (childConditions.length === 0) {
    return null
  }

  if (childConditions.length === 1) {
    return childConditions[0]
  }

  return optimizedExpression
}

function optimizeMatchExpression(
  expression: MatchExpression,
  parameters: QueryParameters,
  options: Required<GraphOptimizationOptions>
): MatchExpression {
  const operator = getOperator(expression)

  const optimizedChildren: Condition[] = []
  for (const condition of expression.conditions) {
    const optimized = optimizeCondition(condition, parameters, options)
    if (optimized) {
      optimizedChildren.push(optimized)
    }
  }

  if (operator === 'OR') {
    const flattened = flattenConditions('OR', optimizedChildren)
    const merged = mergeOrFieldMatchParameters(flattened, parameters, {
      allowCombine: options.mergeOrFieldParameters
    })
    let result: Condition | null = buildOrCondition(merged)

    if (options.dedupeOrBranches && merged.length > 1) {
      result = dedupeOrBranches(merged, parameters) ?? result
    }

    return ensureMatchExpression(result ?? buildOrCondition(merged) ?? { operator: 'OR', conditions: [] })
  }

  const flattened = flattenConditions('AND', optimizedChildren)
  const merged = mergeAndFieldMatchParameters(flattened, parameters, {
    allowCombine: options.mergeOrFieldParameters
  })
  const andCondition = buildAndCondition(merged)

  return ensureMatchExpression(andCondition ?? { operator: 'AND', conditions: [] })
}

function dedupeOrBranches(conditions: Condition[], parameters: QueryParameters): Condition | null {
  const analyses: BranchAnalysis[] = []

  for (const condition of conditions) {
    const analysis = analyseOrBranch(condition, parameters)
    if (!analysis) {
      return null
    }
    analyses.push(analysis)
  }

  if (analyses.length === 0) {
    return null
  }

  let commonKeys = new Set(analyses[0].candidateKeys)
  for (let i = 1; i < analyses.length; i++) {
    commonKeys = intersectSets(commonKeys, analyses[i].candidateKeys)
    if (commonKeys.size === 0) {
      break
    }
  }

  if (commonKeys.size === 0) {
    return null
  }

  const extractedConditions = Array.from(commonKeys, key => analyses[0].candidatesByKey.get(key)!)
  const remainingBranches: Condition[] = []
  let hasEmptyBranch = false

  for (const analysis of analyses) {
    const branchCondition = analysis.buildRemainingCondition(commonKeys)

    if (!branchCondition) {
      hasEmptyBranch = true
      break
    }

    remainingBranches.push(branchCondition)
  }

  if (hasEmptyBranch) {
    return buildAndCondition(extractedConditions)
  }

  const orCondition = buildOrCondition(remainingBranches)

  if (!orCondition) {
    return buildAndCondition(extractedConditions)
  }

  return buildAndCondition([...extractedConditions, orCondition])
}

function analyseOrBranch(condition: Condition, parameters: QueryParameters): BranchAnalysis | null {
  if (isFieldMatchNode(condition)) {
    const key = getFieldMatchKey(condition, parameters)
    if (!key) return null

    return {
      items: [{ condition, candidateKey: key }],
      candidateKeys: new Set([key]),
      candidatesByKey: new Map([[key, condition]]),
      buildRemainingCondition(removeKeys) {
        if (removeKeys.has(key)) {
          return null
        }
        return condition
      }
    }
  }

  const operator = getOperator(condition)
  if (operator === 'OR') {
    return null
  }

  const items: BranchItem[] = []
  const candidateKeys = new Set<string>()
  const candidatesByKey = new Map<string, FieldMatchNode>()

  for (const child of condition.conditions) {
    if (isFieldMatchNode(child)) {
      const key = getFieldMatchKey(child, parameters)
      if (key) {
        items.push({ condition: child, candidateKey: key })
        candidateKeys.add(key)
        if (!candidatesByKey.has(key)) {
          candidatesByKey.set(key, child)
        }
        continue
      }
    }
    items.push({ condition: child })
  }

  if (items.length === 0) {
    return {
      items: [],
      candidateKeys: new Set(),
      candidatesByKey: new Map(),
      buildRemainingCondition() {
        return null
      }
    }
  }

  return {
    items,
    candidateKeys,
    candidatesByKey,
    buildRemainingCondition(removeKeys: Set<string>) {
      const remaining: Condition[] = []

      for (const item of items) {
        if (item.candidateKey && removeKeys.has(item.candidateKey)) {
          continue
        }
        remaining.push(item.condition)
      }

      return buildAndCondition(remaining)
    }
  }
}

interface BranchItem {
  condition: Condition
  candidateKey?: string
}

interface BranchAnalysis {
  items: BranchItem[]
  candidateKeys: Set<string>
  candidatesByKey: Map<string, FieldMatchNode>
  buildRemainingCondition(removeKeys: Set<string>): Condition | null
}

function getFieldMatchKey(condition: FieldMatchNode, parameters: QueryParameters): string | null {
  const termValue = condition.term ? resolveParameterValue(parameters, condition.term) : undefined
  const termMinValue = condition.term_min ? resolveParameterValue(parameters, condition.term_min) : undefined
  const termMaxValue = condition.term_max ? resolveParameterValue(parameters, condition.term_max) : undefined

  const keyData = {
    field: condition.field,
    fieldPrefix: condition.fieldPrefix ?? null,
    termPrefix: condition.termPrefix ?? null,
    similarity: condition.similarity ?? null,
    boost: condition.boost ?? null,
    inclusive: (condition as any).inclusive ?? null,
    term: termValue ?? null,
    term_min: termMinValue ?? null,
    term_max: termMaxValue ?? null
  }

  return JSON.stringify(keyData)
}

function resolveParameterValue(parameters: QueryParameters, reference: QueryParameterReference) {
  const id = reference.id as any

  if (Array.isArray(parameters)) {
    return parameters[Number(id)]
  }

  return parameters[id]
}

function buildAndCondition(conditions: Condition[]): Condition | null {
  const filtered = conditions.filter(Boolean)

  if (filtered.length === 0) {
    return null
  }

  if (filtered.length === 1) {
    return filtered[0]
  }

  return { operator: 'AND', conditions: filtered }
}

function buildOrCondition(conditions: Condition[]): Condition | null {
  const filtered = conditions.filter(Boolean)

  if (filtered.length === 0) {
    return null
  }

  if (filtered.length === 1) {
    return filtered[0]
  }

  return { operator: 'OR', conditions: filtered }
}

function flattenConditions(target: ExplicitOperator, conditions: Condition[]): Condition[] {
  const flattened: Condition[] = []

  for (const condition of conditions) {
    if (isMatchExpressionNode(condition) && getOperator(condition) === target) {
      flattened.push(...condition.conditions)
    } else {
      flattened.push(condition)
    }
  }

  return flattened
}

function ensureMatchExpression(condition: Condition): MatchExpression {
  if (isFieldMatchNode(condition)) {
    return { conditions: [condition] }
  }

  return condition
}

function getOperator(expression: MatchExpression): ExplicitOperator {
  return (expression as MatchExpressionMultiMatch).operator ?? 'AND'
}

function isMatchExpressionNode(condition: Condition): condition is MatchExpression {
  return typeof condition === 'object' && condition !== null && 'conditions' in condition
}

function intersectSets<T>(a: Set<T>, b: Set<T>): Set<T> {
  const intersection = new Set<T>()

  for (const value of a) {
    if (b.has(value)) {
      intersection.add(value)
    }
  }

  return intersection
}

function mergeOrFieldMatchParameters(
  conditions: Condition[],
  parameters: QueryParameters,
  options: { allowCombine?: boolean } = {}
): Condition[] {
  const allowCombine = options.allowCombine !== false

  if (!allowCombine) {
    return conditions
  }

  const groups = new Map<string, { nodes: FieldMatchNode[]; indices: number[] }>()
  const replacements = new Map<number, Condition>()
  const skipIndices = new Set<number>()

  conditions.forEach((condition, index) => {
    if (!isFieldMatchNode(condition)) {
      return
    }

    if (!condition.term || condition.term_min || condition.term_max) {
      return
    }

    const groupKey = buildFieldMatchGroupKey(condition)
    const group = groups.get(groupKey)

    if (group) {
      group.nodes.push(condition)
      group.indices.push(index)
    } else {
      groups.set(groupKey, { nodes: [condition], indices: [index] })
    }
  })

  for (const group of groups.values()) {
    if (group.nodes.length < 2) {
      continue
    }

    const aggregated = aggregateTermValues(group.nodes, group.indices, parameters)
    if (!aggregated) {
      continue
    }

    const { uniqueValues, representativeIndex } = aggregated

    if (uniqueValues.length === 0) {
      continue
    }

    if (representativeIndex != null) {
      for (const index of group.indices) {
        if (index !== representativeIndex) {
          skipIndices.add(index)
        }
      }
      continue
    }

    const combinedReference = createCombinedParameter(parameters, uniqueValues)
    const firstIndex = group.indices[0]
    const baseNode = group.nodes[0]
    const mergedNode: FieldMatchNode = {
      ...baseNode,
      term: combinedReference
    }

    replacements.set(firstIndex, mergedNode)
    for (const index of group.indices) {
      skipIndices.add(index)
    }
  }

  const mergedConditions: Condition[] = []

  for (let i = 0; i < conditions.length; i++) {
    const replacement = replacements.get(i)
    if (replacement) {
      mergedConditions.push(replacement)
      continue
    }

    if (skipIndices.has(i)) {
      continue
    }

    mergedConditions.push(conditions[i])
  }

  return mergedConditions
}

function mergeAndFieldMatchParameters(
  conditions: Condition[],
  parameters: QueryParameters,
  options: { allowCombine?: boolean } = {}
): Condition[] {
  const allowCombine = options.allowCombine !== false

  if (!allowCombine) {
    return conditions
  }

  const groups = new Map<string, { nodes: FieldMatchNode[]; indices: number[] }>()
  const replacements = new Map<number, Condition>()
  const skipIndices = new Set<number>()

  conditions.forEach((condition, index) => {
    if (!isFieldMatchNode(condition)) {
      return
    }

    if (!condition.term || condition.term_min || condition.term_max) {
      return
    }

    const groupKey = buildFieldMatchGroupKey(condition)
    const group = groups.get(groupKey)

    if (group) {
      group.nodes.push(condition)
      group.indices.push(index)
    } else {
      groups.set(groupKey, { nodes: [condition], indices: [index] })
    }
  })

  for (const group of groups.values()) {
    if (group.nodes.length < 2) {
      continue
    }

    const intersected = intersectTermValues(group.nodes, group.indices, parameters)
    if (!intersected) {
      continue
    }

    const { intersectedValues, representativeIndex } = intersected

    // If intersection is empty, create a merged condition with empty array
    // The index lookup will return no documents
    if (intersectedValues.length === 0) {
      const emptyReference = createCombinedParameter(parameters, [])
      const emptyNode: FieldMatchNode = {
        ...group.nodes[0],
        term: emptyReference
      }
      replacements.set(group.indices[0], emptyNode)
      for (let i = 1; i < group.indices.length; i++) {
        skipIndices.add(group.indices[i])
      }
      continue
    }

    if (representativeIndex != null) {
      for (const index of group.indices) {
        if (index !== representativeIndex) {
          skipIndices.add(index)
        }
      }
      continue
    }

    const combinedReference = createCombinedParameter(parameters, intersectedValues)
    const firstIndex = group.indices[0]
    const baseNode = group.nodes[0]
    const mergedNode: FieldMatchNode = {
      ...baseNode,
      term: combinedReference
    }

    replacements.set(firstIndex, mergedNode)
    for (const index of group.indices) {
      skipIndices.add(index)
    }
  }

  const mergedConditions: Condition[] = []

  for (let i = 0; i < conditions.length; i++) {
    const replacement = replacements.get(i)
    if (replacement) {
      mergedConditions.push(replacement)
      continue
    }

    if (skipIndices.has(i)) {
      continue
    }

    mergedConditions.push(conditions[i])
  }

  return mergedConditions
}

function buildFieldMatchGroupKey(condition: FieldMatchNode): string {
  return JSON.stringify({
    field: condition.field,
    fieldPrefix: condition.fieldPrefix ?? null,
    termPrefix: condition.termPrefix ?? null,
    similarity: condition.similarity ?? null,
    boost: condition.boost ?? null,
    inclusive: (condition as any).inclusive ?? null,
    termQuotes: (condition as any).termQuotes ?? null
  })
}

function aggregateTermValues(
  nodes: FieldMatchNode[],
  indices: number[],
  parameters: QueryParameters
): { uniqueValues: string[]; representativeIndex?: number } | null {
  const combinedValues: string[] = []
  const combinedKeys = new Set<string>()
  const nodeValueSets: Array<{ keys: Set<string>; index: number }> = []

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (!node.term) {
      return null
    }

    const resolved = resolveParameterValue(parameters, node.term)
    if (resolved == null) {
      return null
    }

    const values = Array.isArray(resolved) ? resolved : [resolved]
    const valueKeys = new Set<string>()

    for (const value of values) {
      const stringValue = `${value}`
      valueKeys.add(stringValue)

      if (!combinedKeys.has(stringValue)) {
        combinedKeys.add(stringValue)
        combinedValues.push(stringValue)
      }
    }

    nodeValueSets.push({ keys: valueKeys, index: indices[i] })
  }

  if (combinedValues.length === 0) {
    return null
  }

  let representativeIndex: number | undefined

  for (const { keys, index } of nodeValueSets) {
    if (keys.size !== combinedKeys.size) {
      continue
    }

    let matches = true
    for (const key of combinedKeys) {
      if (!keys.has(key)) {
        matches = false
        break
      }
    }

    if (matches) {
      representativeIndex = index
      break
    }
  }

  return { uniqueValues: combinedValues, representativeIndex }
}

function intersectTermValues(
  nodes: FieldMatchNode[],
  indices: number[],
  parameters: QueryParameters
): { intersectedValues: string[]; representativeIndex?: number } | null {
  if (nodes.length === 0) {
    return null
  }

  // Start with the first node's values
  const firstNode = nodes[0]
  if (!firstNode.term) {
    return null
  }

  const firstResolved = resolveParameterValue(parameters, firstNode.term)
  if (firstResolved == null) {
    return null
  }

  const firstValues = Array.isArray(firstResolved) ? firstResolved : [firstResolved]
  let intersectedSet = new Set(firstValues.map(v => `${v}`))

  const nodeValueSets: Array<{ keys: Set<string>; index: number }> = [
    { keys: new Set(firstValues.map(v => `${v}`)), index: indices[0] }
  ]

  // Intersect with all other nodes
  for (let i = 1; i < nodes.length; i++) {
    const node = nodes[i]
    if (!node.term) {
      return null
    }

    const resolved = resolveParameterValue(parameters, node.term)
    if (resolved == null) {
      return null
    }

    const values = Array.isArray(resolved) ? resolved : [resolved]
    const valueKeys = new Set(values.map(v => `${v}`))

    // Perform intersection
    const newIntersection = new Set<string>()
    for (const value of intersectedSet) {
      if (valueKeys.has(value)) {
        newIntersection.add(value)
      }
    }

    intersectedSet = newIntersection
    nodeValueSets.push({ keys: valueKeys, index: indices[i] })

    // Early exit if intersection is empty
    if (intersectedSet.size === 0) {
      break
    }
  }

  const intersectedValues = Array.from(intersectedSet)

  // Check if any node already has exactly these values (representative)
  let representativeIndex: number | undefined

  for (const { keys, index } of nodeValueSets) {
    if (keys.size !== intersectedSet.size) {
      continue
    }

    let matches = true
    for (const key of intersectedSet) {
      if (!keys.has(key)) {
        matches = false
        break
      }
    }

    if (matches) {
      representativeIndex = index
      break
    }
  }

  return { intersectedValues, representativeIndex }
}

function createCombinedParameter(parameters: QueryParameters, values: string[]): QueryParameterReference {
  if (Array.isArray(parameters)) {
    const id = parameters.length
    parameters[id] = values
    return { id }
  }

  // Find the next available numeric key
  let counter = 0
  while (Object.prototype.hasOwnProperty.call(parameters, counter.toString())) {
    counter++
  }

  const key = counter.toString()
  parameters[key] = values
  return { id: counter }  // Return numeric id, even though object key is string
}
